import { useDraggable } from '@dnd-kit/core';
import {
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
  useHover,
  useInteractions,
  useRole
} from '@floating-ui/react';
import { SunIcon } from '@heroicons/react/24/outline';
import { SkylightItemType } from 'data/types';
import { useCallback, useState } from 'react';

type SkylightBadgeProps = {
  skylights: Partial<SkylightItemType>[];
  spaceId: string;
  onContextMenu: (e: React.MouseEvent) => void;
  zoom?: number;
};

export default function SkylightBadge({
  skylights,
  spaceId,
  onContextMenu,
  zoom = 1
}: SkylightBadgeProps) {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const count = skylights.length;
  const skylightUniqueIds = skylights
    .map((s) => s.uniqueId)
    .filter(Boolean) as string[];

  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: `skylight-badge-${spaceId}`,
      data: {
        type: 'grid-skylight',
        spaceId,
        skylightUniqueIds
      }
    });

  const { refs, floatingStyles, context } = useFloating({
    open: isTooltipOpen,
    onOpenChange: setIsTooltipOpen,
    placement: 'top',
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip(), shift({ padding: 5 })]
  });

  const hover = useHover(context, { move: false });
  const role = useRole(context, { role: 'tooltip' });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    role
  ]);

  const refCallback = useCallback(
    (node: HTMLDivElement | null) => {
      setNodeRef(node);
      refs.setReference(node);
    },
    [setNodeRef, refs]
  );

  let tooltipText: string;
  if (count === 1 && skylights[0].length && skylights[0].width) {
    tooltipText = `Skylight: ${skylights[0].length}ft x ${skylights[0].width}ft`;
  } else {
    tooltipText = `${count} Skylight${count !== 1 ? 's' : ''}`;
  }

  const style = {
    transform: transform
      ? `translate3d(${transform.x / zoom}px, ${transform.y / zoom}px, 0)`
      : undefined,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? 'grabbing' : 'grab'
  };

  return (
    <>
      <div
        ref={refCallback}
        className='absolute bottom-1 right-1 z-[10] flex items-center gap-0.5 rounded bg-yellow-100 px-1 py-0.5'
        style={{ ...style, pointerEvents: 'auto' }}
        onContextMenu={onContextMenu}
        {...attributes}
        {...listeners}
        {...getReferenceProps()}
      >
        <SunIcon className='h-3 w-3 text-yellow-700' />
        {count > 1 && (
          <span className='text-[10px] font-medium text-yellow-700'>
            {count}
          </span>
        )}
      </div>
      <FloatingPortal>
        {isTooltipOpen && (
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className='z-50 rounded bg-[#444] px-2 py-1 text-sm text-white'
            {...getFloatingProps()}
          >
            {tooltipText}
          </div>
        )}
      </FloatingPortal>
    </>
  );
}
